import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * `useState` that survives a reload.
 *
 * A half-keyed purchase invoice is twenty minutes of somebody's afternoon, and
 * a reload - a dropped connection, a mis-hit F5, a tab the browser decided to
 * discard - threw it away. This keeps the draft on the device and hands it
 * back on the next mount.
 *
 * ## What it is, and is not
 *
 * It is a DRAFT store: the state as last seen, on this device, for this key.
 * It is not a sync layer, not shared between devices, and never the record of
 * truth - the server is, the moment the draft is saved. Anything that must
 * not be lost belongs in an API call, not here.
 *
 * ## The codec
 *
 * State rarely survives `JSON.stringify` untouched: a `Dayjs` becomes a string
 * and does not come back, a Map becomes `{}`. So the caller supplies how to
 * flatten the state and how to rebuild it, and a `version` that is bumped
 * whenever the shape changes. A stored draft from an older shape is dropped
 * rather than half-read into something the page cannot use.
 *
 * `decode` may return null to reject what it finds, and `isEmpty` says when
 * there is nothing worth keeping - a page with one blank line on it does not
 * need a draft, and keeping one would mean announcing "draft restored" to
 * somebody who had typed nothing.
 *
 * ## Cost
 *
 * Writes are debounced, so keying a batch number costs one serialisation when
 * the typing pauses rather than one per keystroke. A pending write is flushed
 * on unmount, so navigating away mid-word does not lose the word. Storage is
 * wrapped in try/catch throughout: a private window or a full quota must never
 * take the page down with it.
 */

export type Codec<S, J> = {
  /** Bump when the stored shape changes. Older drafts are discarded. */
  version: number;
  encode: (state: S) => J;
  /** Return null to reject a stored draft. */
  decode: (raw: J) => S | null;
  /** True when there is nothing worth keeping; the key is removed instead. */
  isEmpty?: (state: S) => boolean;
};

type Envelope<J> = { v: number; at: number; data: J };

export type DraftHandle = {
  /** The state came from storage rather than from `initial`. Fixed at mount. */
  restored: boolean;
  /** When the stored draft was last written, or null if there was none. */
  restoredAt: number | null;
  /**
   * Forget the stored draft. The in-memory state is left alone: resetting it is
   * the page's business, and an empty state will not be re-persisted anyway
   * (see `isEmpty`).
   */
  clear: () => void;
};

const DEBOUNCE_MS = 300;

function read<S, J>(key: string, codec: Codec<S, J>): { state: S; at: number } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as Envelope<J>;
    if (!envelope || envelope.v !== codec.version) return null;
    const state = codec.decode(envelope.data);
    return state === null ? null : { state, at: envelope.at };
  } catch {
    return null;
  }
}

function write<S, J>(key: string, codec: Codec<S, J>, state: S): void {
  try {
    if (codec.isEmpty?.(state)) {
      localStorage.removeItem(key);
      return;
    }
    const envelope: Envelope<J> = { v: codec.version, at: Date.now(), data: codec.encode(state) };
    localStorage.setItem(key, JSON.stringify(envelope));
  } catch {
    /* Quota, private mode, disabled storage. The page keeps working. */
  }
}

/**
 * @param key   Where it lives. Include whatever scopes the draft - the
 *              pharmacy, the record being edited - so two of them cannot
 *              collide. Must be stable for the life of the component.
 * @param initial  What to start from when there is no draft.
 * @param codec    How to flatten and rebuild the state; see `Codec`.
 */
export function usePersistentState<S, J = unknown>(
  key: string,
  initial: () => S,
  codec: Codec<S, J>,
): [S, React.Dispatch<React.SetStateAction<S>>, DraftHandle] {
  /* Held in refs so neither can churn the effects below. */
  const codecRef = useRef(codec);
  codecRef.current = codec;

  const restoredRef = useRef<{ restored: boolean; at: number | null }>({
    restored: false,
    at: null,
  });

  const [state, setState] = useState<S>(() => {
    const found = read(key, codecRef.current);
    if (found) {
      restoredRef.current = { restored: true, at: found.at };
      return found.state;
    }
    return initial();
  });

  const timer = useRef<number | null>(null);
  const latest = useRef(state);
  latest.current = state;

  useEffect(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      write(key, codecRef.current, latest.current);
    }, DEBOUNCE_MS);
  }, [key, state]);

  /* Flush on unmount, so the last keystroke before navigating away lands. */
  useEffect(
    () => () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
        write(key, codecRef.current, latest.current);
      }
    },
    [key],
  );

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* See `write`. */
    }
  }, [key]);

  return [
    state,
    setState,
    { restored: restoredRef.current.restored, restoredAt: restoredRef.current.at, clear },
  ];
}
